'use client'

import { XIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import type * as React from 'react'

import { useDocumentDirection } from '../../hooks/use-document-direction'
import { cn } from '../../lib/utils'

function Sheet({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-scrim',
        'duration-[var(--motion-base)] data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  )
}

function SheetContent({
  side = 'end',
  className,
  children,
  closeLabel,
  dir: providedDir,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  side?: 'start' | 'end'
  closeLabel: string
}) {
  const documentDirection = useDocumentDirection()
  const dir = providedDir ?? documentDirection

  return (
    <DialogPrimitive.Portal>
      <SheetOverlay />
      <DialogPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          'fixed inset-y-0 z-50 flex h-full w-full max-w-md flex-col bg-popover text-popover-foreground',
          // The modal elevation (DESIGN.md §4): a sheet is modal like the
          // dialog, so it takes the modal shadow and no border, over the
          // scrim. It slides on the drawer's slow duration.
          'shadow-modal',
          side === 'start' ? 'start-0' : 'end-0',
          'duration-[var(--motion-slow)] data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          side === 'start'
            ? 'data-[state=open]:slide-in-from-start data-[state=closed]:slide-out-to-start'
            : 'data-[state=open]:slide-in-from-end data-[state=closed]:slide-out-to-end',
          className,
        )}
        {...props}
        dir={dir}
      >
        {children}
        <DialogPrimitive.Close
          data-slot="sheet-close"
          className={cn(
            'absolute top-4 end-4 inline-flex size-control items-center justify-center rounded-sm [&_svg]:size-4',
            'text-muted-foreground transition-ui hover:bg-accent hover:text-foreground',
            'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ring',
          )}
        >
          <XIcon />
          <span className="sr-only">{closeLabel}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        'flex flex-col gap-2 border-b border-border px-6 pt-6 pb-4 pe-16 text-start',
        className,
      )}
      {...props}
    />
  )
}

/** Padded body for prose and form content. Tables and lists can opt out with `p-0`. */
function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sheet-body"
      className={cn('min-h-0 overflow-y-auto p-6', className)}
      {...props}
    />
  )
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="sheet-title"
      className={cn('text-lg font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="sheet-description"
      className={cn('text-body text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
}
